import { Flex, FormGroup, Grid, Paragraph } from "@eworkit/iris";
import { Formik, FormikProps } from "formik";
import React from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";

import { IEditableSectionRenderProps, useEditableSubmit } from "@/components/EditableSection";
import { FileUploadStateItem } from "@/components/Files/upload/fileUploadReducer";
import { FormikSingleFileUpload } from "@/components/Files/upload/SingleFileUpload";
import { FormikDatePicker, FormikError, VALIDATION_TYPES } from "@/components/Formik";
import { FormikNumberInput } from "@/components/Formik/FormikNumberInput";
import { Attachment, IAttachment, TAttachmentPurpose } from "@/data/attachments";
import { ICompanyInsurance } from "@/data/companies";
import { Group } from "@/eworkitComponents";
import { FormikCheckbox } from "@/formikComponents";
import { useFormats } from "@/hooks/Formats";

import { ICompanyFormikProps } from "../CompanyData/types";

export interface IInsuranceFormData extends Omit<ICompanyInsurance, "file" | "amount"> {
  file?: FileUploadStateItem<IAttachment>;
}

export const insuranceSchema = Yup.object().shape({
  hasInsurance: Yup.boolean().optional(),
  file: Yup.object().when("hasInsurance", {
    is: true,
    then: Yup.object()
      .test({
        message: VALIDATION_TYPES.UPLOAD_PROGRESS,
        test: (value: FileUploadStateItem<Attachment>) => !value || value.progress === 100,
      })
      .test({
        message: VALIDATION_TYPES.UPLOAD_ERRORS,
        test: (value: FileUploadStateItem<Attachment>) => !value?.error,
      })
      .test({
        message: VALIDATION_TYPES.REQUIRED,
        test: (value: FileUploadStateItem<Attachment>) => Boolean(value),
      }),
    otherwise: Yup.object().notRequired(),
  }),
  expirationDate: Yup.string().when("hasInsurance", {
    is: true,
    then: Yup.string().required(VALIDATION_TYPES.REQUIRED),
    otherwise: Yup.string().notRequired(),
  }),
  amount: Yup.number().when("hasInsurance", {
    is: true,
    then: Yup.number().required(VALIDATION_TYPES.REQUIRED),
    otherwise: Yup.number().notRequired(),
  }),
  accepted: Yup.boolean().when("hasInsurance", {
    is: true,
    then: Yup.boolean().oneOf([true], VALIDATION_TYPES.CHECKED).required(VALIDATION_TYPES.REQUIRED),
    otherwise: Yup.boolean().notRequired(),
  }),
});

interface IInsuranceFormProps extends IEditableSectionRenderProps {
  form: FormikProps<IInsuranceFormData>;
}

const InsuranceForm = ({ form, editableProps }: IInsuranceFormProps) => {
  const { t } = useTranslation("session");
  const { formatDate } = useFormats();

  useEditableSubmit(form, editableProps);

  const handleExpirationDateChange = (date: Date | null) => {
    date?.setHours(2);
    const formattedValue = date?.toISOString().split("T")[0];

    form.setFieldValue("expirationDate", formattedValue);
  };

  return (
    <Flex flexDirection="column" gap="sm" mt="xxl">
      <FormGroup>
        <FormikCheckbox name="hasInsurance" checked={form.values.hasInsurance} size="lg">
          <Paragraph size="p-md">{t("USERS.SETTINGS.INSURANCE_DETAILS.FORM.HAS_INSURANCE")}</Paragraph>
        </FormikCheckbox>

        <FormikError name="hasInsurance" data-test="error-hasInsurance" />
      </FormGroup>

      {form.values.hasInsurance && (
        <>
          <Grid columns="1fr 1fr" gap="sm">
            <Group direction="column" required label={t("USERS.SETTINGS.INSURANCE_DETAILS.FORM.EXPIRATION_DATE")}>
              <FormikDatePicker
                name="expirationDate"
                onChange={(value: Date | null) => handleExpirationDateChange(value)}
                value={form.values.expirationDate ? new Date(form.values.expirationDate) : null}>
                {formatDate(form.values.expirationDate)}
              </FormikDatePicker>

              <FormikError name="expirationDate" data-test="error-expirationDate" />
            </Group>

            <Group direction="column" required label={t("USERS.SETTINGS.INSURANCE_DETAILS.FORM.AMOUNT")}>
              <FormikNumberInput clearable={false} required name="amount" size="md" />
            </Group>
          </Grid>

          <FormGroup>
            <FormikSingleFileUpload
              maxSize={6}
              name="file"
              attachment={form.values.file?.attachment}
              attachmentType={TAttachmentPurpose.ATTACHMENT}
            />

            <FormikError name="file" data-test="error-file" />
          </FormGroup>

          <FormGroup>
            <FormikCheckbox required name="accepted" checked={form.values.accepted} size="lg">
              <Paragraph size="p-md">
                {t("USERS.SETTINGS.INSURANCE_DETAILS.FORM.CONFIRMATION")} <Paragraph color="r500">*</Paragraph>
              </Paragraph>
            </FormikCheckbox>

            <FormikError name="accepted" data-test="error-accepted" />
          </FormGroup>
        </>
      )}
    </Flex>
  );
};

interface IBankingDetailsFormikProps extends ICompanyFormikProps<IInsuranceFormData>, IEditableSectionRenderProps {}

export const CompanyInsuranceSectionEdit = ({ onSubmit, initialValues, ...rest }: IBankingDetailsFormikProps) => (
  <Formik enableReinitialize onSubmit={onSubmit} initialValues={initialValues} validationSchema={insuranceSchema}>
    {(form: FormikProps<IInsuranceFormData>) => <InsuranceForm {...rest} form={form} />}
  </Formik>
);
